/**
 * DocumentAnnotator
 * // Covers D-Q22 (modes), D-Q26 (actor model)
 *
 * Renders a PDF using pdfjs-dist canvas rendering, with an interactive
 * annotation overlay layer on top. Annotations are stored on the backend;
 * the original PDF file is never modified.
 *
 * Coordinate system: annotations use 0-1000 normalised units relative to
 * the rendered page canvas. This decouples annotation positions from actual
 * PDF point dimensions and display zoom level.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth/AuthProvider';
import { toast } from 'sonner';
import {
  AnnotationToolbar,
  type AnnotationMode,
  type StampType,
  STAMP_LABELS,
} from './AnnotationToolbar';
import { DocumentReviewSendDialog } from './DocumentReviewSendDialog';
import { ExternalStampSection } from './ExternalStampSection';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface Annotation {
  id: string;
  document_review_id: string;
  page_number: number;
  type: 'text' | 'stamp' | 'freehand';
  coords: Record<string, any>;
  data: Record<string, any>;
  actor_id: string;
  created_at: string;
}

interface PendingTextInput {
  x: number; // canvas-relative pixels
  y: number;
  pageNumber: number;
}

interface DocumentAnnotatorProps {
  documentReviewId: string;
  /** Download URL or signed URL for the PDF */
  pdfUrl: string;
  mode: 'comment' | 'approve' | 'sign';
  /** Project ID needed by the send dialog to resolve engineer contacts. */
  projectId?: string;
  onClose?: () => void;
  reviewStatus?: string;
  /** True when rendered from the customer-facing portal. Hides admin-only
   *  actions (Export PDF, Send for Review, R6 external-stamp upload). */
  isPortal?: boolean;
  /** When the review record is already locked + externally-stamped, parent
   *  passes these so the cyan badge + audit cert button surface on re-open
   *  (the in-session state only covers the active→confirmed transition). */
  externallyStampedPdfRef?: string | null;
  auditCertPdfRef?: string | null;
  /** Portal auth context — when present, annotation list/create are routed
   *  through `api.portalReviews.*` (token-gated, no Firebase auth) instead of
   *  the admin endpoints. Delete is a no-op in portal mode (R1: customers
   *  cannot delete annotations). */
  portalContext?: {
    viewToken: string;
    actorId: string;
    password?: string;
    /** Tenant doc-review setting passed through the portal action payload.
     *  When false (Canadian permit-set workflow), the in-canvas stamp tool
     *  is hidden on the customer side and approve mode uses the Confirm
     *  Approval dialog instead. Defaults to true if not provided. */
    inCanvasStampsEnabled?: boolean;
  };
}

// ---------------------------------------------------------------------------
// Stamp rendering helpers for canvas overlay
// ---------------------------------------------------------------------------

const STAMP_COLORS: Record<StampType, string> = {
  approved: '#008a00',
  approved_as_noted: '#0074cd',
  revise_resubmit: '#f28b00',
  rejected: '#cc0000',
};

const COORD_SCALE = 1000; // normalised coordinate space

function normToCanvas(n: number, dim: number): number {
  return (n / COORD_SCALE) * dim;
}

function canvasToNorm(c: number, dim: number): number {
  return (c / dim) * COORD_SCALE;
}

// Read the portal customer's display name captured via IdentityCapture and
// stored under `portal_actor_{viewToken}`. Falls back to "Customer" so the
// stamp label never shows "Architect" for portal-side stamps.
function getPortalActorName(viewToken: string): string {
  try {
    const raw = localStorage.getItem(`portal_actor_${viewToken}`);
    if (!raw) return 'Customer';
    const parsed = JSON.parse(raw);
    return parsed?.displayName || 'Customer';
  } catch {
    return 'Customer';
  }
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function DocumentAnnotator({
  documentReviewId,
  pdfUrl,
  mode: reviewMode,
  projectId,
  onClose,
  reviewStatus,
  isPortal = false,
  portalContext,
  externallyStampedPdfRef,
  auditCertPdfRef,
}: DocumentAnnotatorProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // PDF rendering state
  const [pdfDocument, setPdfDocument] = useState<any | null>(null);
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pdfLoading, setPdfLoading] = useState(true);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [renderScale, setRenderScale] = useState(1.5);

  // Annotation interaction state — default toolbar mode driven by reviewMode prop
  const [mode, setMode] = useState<AnnotationMode>(() => {
    if (reviewMode === 'approve') return 'stamp';
    if (reviewMode === 'sign') return 'freehand'; // signature tool added in Phase 4
    return 'pointer';
  });
  const [stampType, setStampType] = useState<StampType>('approved');
  const [freehandColor, setFreehandColor] = useState('#e63946');
  const [drawing, setDrawing] = useState(false);
  const [currentPath, setCurrentPath] = useState<[number, number][]>([]);
  const [pendingText, setPendingText] = useState<PendingTextInput | null>(null);
  const [textInputValue, setTextInputValue] = useState('');
  const [exporting, setExporting] = useState(false);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  // Approve fast-path (D-Q5 AC#6): confirm "approve without markup?"
  const [approveFastPathOpen, setApproveFastPathOpen] = useState(false);
  // F1: optional condition note captured during the stamp-free confirm-approval path
  const [conditionNote, setConditionNote] = useState('');
  // R5 AC#2 / D-Q9: page-cap soft-warn for 51-200 page documents
  const [pageCapWarnOpen, setPageCapWarnOpen] = useState(false);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const overlayRef = useRef<HTMLCanvasElement>(null);
  const textInputRef = useRef<HTMLInputElement>(null);

  // Track active pointer for palm rejection (single-touch drawing guard)
  const activePointerIdRef = useRef<number | null>(null);

  // ---------------------------------------------------------------------------
  // Mobile canvas: pointer events + touch-action scoping (D-Q14 / AC #8)
  // Pointer Events API unifies mouse, touch, and stylus into one event model.
  // touch-action: none is scoped to drawing modes only so page scroll still works.
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const overlay = overlayRef.current;
    if (!overlay) return;
    overlay.style.touchAction = (mode === 'freehand') ? 'none' : 'auto';
  }, [mode]);

  useEffect(() => {
    const overlay = overlayRef.current;
    if (!overlay) return;

    function getPointerCoords(e: PointerEvent): { x: number; y: number } {
      const rect = overlay!.getBoundingClientRect();
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    }

    // Persisted across move/up; we collect points here in a ref so the
    // pointer-up handler can read the final stroke and POST without racing
    // React state. Without this, the previous code persisted state only
    // through setCurrentPath; pointerup just flipped `drawing` to false and
    // the mutation lived in the (now-bypassed) React onMouseUp handler.
    let pointerPath: [number, number][] = [];

    function onPointerDown(e: PointerEvent) {
      if (mode !== 'freehand') return;
      if (activePointerIdRef.current !== null) return; // palm rejection: ignore 2nd contact
      e.preventDefault();
      overlay!.setPointerCapture(e.pointerId);
      activePointerIdRef.current = e.pointerId;

      const { x, y } = getPointerCoords(e);
      pointerPath = [[
        canvasToNorm(x, overlay!.width),
        canvasToNorm(y, overlay!.height),
      ]];
      setDrawing(true);
      setCurrentPath(pointerPath);
    }

    function onPointerMove(e: PointerEvent) {
      if (e.pointerId !== activePointerIdRef.current) return;
      if (mode !== 'freehand') return;
      e.preventDefault();

      const { x, y } = getPointerCoords(e);
      pointerPath = [
        ...pointerPath,
        [canvasToNorm(x, overlay!.width), canvasToNorm(y, overlay!.height)],
      ];
      setCurrentPath(pointerPath);
    }

    function onPointerUp(e: PointerEvent) {
      if (e.pointerId !== activePointerIdRef.current) return;
      activePointerIdRef.current = null;
      setDrawing(false);

      // Persist the stroke. The native pointer handler is the canonical path
      // for touch/stylus AND for any pointer-emitting input (Playwright,
      // some Chromium mouse paths). Mirrors handleMouseUp's mutation call
      // so the freehand stroke actually saves — previously the pointer path
      // never created an annotation because pointerdown's preventDefault
      // suppressed React's synthesised onMouseDown/Up.
      if (mode === 'freehand' && pointerPath.length >= 2) {
        createAnnotation.mutate({
          page_number: currentPage,
          type: 'freehand',
          data: {
            points: pointerPath,
            color: freehandColor,
            strokeWidth: 2,
          },
        });
      }
      pointerPath = [];
      setCurrentPath([]);
    }

    function onPointerCancel(e: PointerEvent) {
      if (e.pointerId !== activePointerIdRef.current) return;
      activePointerIdRef.current = null;
      setDrawing(false);
      pointerPath = [];
      setCurrentPath([]);
    }

    overlay.addEventListener('pointerdown', onPointerDown, { passive: false });
    overlay.addEventListener('pointermove', onPointerMove, { passive: false });
    overlay.addEventListener('pointerup', onPointerUp);
    overlay.addEventListener('pointercancel', onPointerCancel);

    return () => {
      overlay.removeEventListener('pointerdown', onPointerDown);
      overlay.removeEventListener('pointermove', onPointerMove);
      overlay.removeEventListener('pointerup', onPointerUp);
      overlay.removeEventListener('pointercancel', onPointerCancel);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, overlayRef.current, currentPage, freehandColor]);

  // ---------------------------------------------------------------------------
  // Load PDF
  // ---------------------------------------------------------------------------

  useEffect(() => {
    let cancelled = false;

    async function loadPdf() {
      setPdfLoading(true);
      setPdfError(null);

      try {
        // Dynamic import to avoid SSR issues and allow code-splitting
        const pdfjsLib = await import('pdfjs-dist');

        // Configure worker — Vite resolves the worker URL at build time
        pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
          'pdfjs-dist/build/pdf.worker.min.mjs',
          import.meta.url
        ).toString();

        const loadingTask = pdfjsLib.getDocument({
          url: pdfUrl,
          withCredentials: false,
        });

        const doc = await loadingTask.promise;

        if (!cancelled) {
          setPdfDocument(doc);
          setNumPages(doc.numPages);
          setCurrentPage(1);
          setPdfLoading(false);
          if (doc.numPages > 50 && doc.numPages <= 200) {
            setPageCapWarnOpen(true);
          }
        }
      } catch (err: any) {
        if (!cancelled) {
          setPdfError(err.message || 'Failed to load PDF');
          setPdfLoading(false);
        }
      }
    }

    loadPdf();
    return () => {
      cancelled = true;
    };
  }, [pdfUrl]);

  // ---------------------------------------------------------------------------
  // Render PDF page to canvas
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!pdfDocument || !canvasRef.current) return;

    let cancelled = false;

    async function renderPage() {
      const page = await pdfDocument.getPage(currentPage);
      if (cancelled) return;

      const viewport = page.getViewport({ scale: renderScale });

      const canvas = canvasRef.current!;
      const ctx = canvas.getContext('2d')!;
      canvas.width = viewport.width;
      canvas.height = viewport.height;

      // Also size the overlay canvas
      if (overlayRef.current) {
        overlayRef.current.width = viewport.width;
        overlayRef.current.height = viewport.height;
      }

      await page.render({ canvasContext: ctx, viewport }).promise;
      if (cancelled) return;

      // Redraw saved annotations on overlay
      redrawAnnotations();
    }

    renderPage();
    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfDocument, currentPage, renderScale]);

  // ---------------------------------------------------------------------------
  // Fetch persisted annotations
  // ---------------------------------------------------------------------------

  // Portal callers use the token-gated bundled endpoint; admins hit the auth-gated
  // annotations list. Default (no portalContext) preserves admin behavior exactly.
  const { data: annotationsData } = useQuery({
    queryKey: [
      'document-review-annotations',
      documentReviewId,
      portalContext ? `portal:${portalContext.actorId}` : 'admin',
    ],
    queryFn: async () => {
      if (portalContext) {
        const { review, annotations } = await api.portalReviews.fetchReviewWithAnnotations(
          documentReviewId,
          portalContext.viewToken,
          portalContext.actorId,
          portalContext.password,
        );
        return { success: true, annotations, total: annotations.length, review };
      }
      return api.documentReviews.annotations.list(documentReviewId);
    },
    enabled: !!documentReviewId && (!portalContext || (!!portalContext.viewToken && !!portalContext.actorId)),
  });

  const savedAnnotations: Annotation[] = (annotationsData?.annotations || []);

  // F1: tenant-level gate for the in-canvas stamp tool. Admin-side only —
  // portal callers don't need this lookup (the tool is gated on the actual
  // toolbar render and portal users never see approve-stamp UI anyway).
  // Returns { in_canvas_stamps_enabled: true } when the tenant has no
  // document_review settings doc — so default behavior matches pre-F1.
  const { data: docReviewSettings } = useQuery({
    queryKey: ['document-review-settings'],
    queryFn: () => api.settings.getDocumentReviewSettings(),
    enabled: !isPortal,
    staleTime: 60_000,
  });
  const inCanvasStampsEnabled = isPortal
    ? (portalContext?.inCanvasStampsEnabled ?? true)
    : (docReviewSettings?.in_canvas_stamps_enabled ?? true);

  // Redraw annotations for the current page onto the overlay canvas
  const redrawAnnotations = useCallback(() => {
    const overlay = overlayRef.current;
    if (!overlay) return;

    const ctx = overlay.getContext('2d')!;
    ctx.clearRect(0, 0, overlay.width, overlay.height);

    const pageAnnotations = savedAnnotations.filter(
      (a) => a.page_number === currentPage
    );

    for (const ann of pageAnnotations) {
      // Skip annotations with no content. Historical drafts sometimes saved
      // placeholder stamp/text/freehand records with empty data (e.g., a
      // quick-stamp gesture that bailed before content arrived). Drawing
      // these emits an empty orange box / blank text bubble on the canvas —
      // F30/F31 in the external-stamp demo flagged this on a sealed review.
      if (!isRenderableAnnotation(ann)) continue;
      drawAnnotationOnCanvas(ctx, ann, overlay.width, overlay.height);
    }
  }, [savedAnnotations, currentPage]);

  // Re-run redraw when annotations or page changes
  useEffect(() => {
    redrawAnnotations();
  }, [redrawAnnotations]);

  // ---------------------------------------------------------------------------
  // Annotation draw helpers
  // ---------------------------------------------------------------------------

  // Actor attribution badge (sales-demo visual): renders a small circle with
  // initials in the upper-right of each annotation so reviewers can tell at a
  // glance which marks are architect vs. customer.
  //
  // Discriminator (no extra fetch needed — derived from existing API shape):
  //   actor_id == null  → architect (admin/user — admin endpoint never sets
  //                       actor_id; see routers/document_reviews.py:319 vs
  //                       portal_reviews.py:275)
  //   actor_id != null  → customer (portal_client — only the portal
  //                       /reviews/{id}/annotations endpoint stamps actor_id)
  const BADGE_RADIUS = 9;
  const ARCHITECT_BADGE_FILL = '#1e3a8a'; // blue-900
  const CUSTOMER_BADGE_FILL = '#16a34a';  // green-600
  const ARCHITECT_INITIALS = 'JB';
  const CUSTOMER_INITIALS = 'SC';

  function drawActorBadge(
    ctx: CanvasRenderingContext2D,
    ann: Annotation,
    cornerX: number,
    cornerY: number,
  ) {
    const isCustomer = !!ann.actor_id;
    const fill = isCustomer ? CUSTOMER_BADGE_FILL : ARCHITECT_BADGE_FILL;
    const initials = isCustomer ? CUSTOMER_INITIALS : ARCHITECT_INITIALS;

    // Save/restore so the badge styling never leaks into the next annotation
    ctx.save();
    ctx.beginPath();
    ctx.arc(cornerX, cornerY, BADGE_RADIUS, 0, Math.PI * 2);
    ctx.fillStyle = fill;
    ctx.fill();
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
    ctx.lineWidth = 1;
    ctx.stroke();

    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 9px Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(initials, cornerX, cornerY + 0.5);
    ctx.restore();
  }

  // Defensive guard against empty annotations leaking onto the canvas.
  // A stamp without a stamp_type renders as a coloured rectangle with no
  // label; a text annotation without content renders as an empty yellow box;
  // a freehand with <2 points renders as nothing useful. Skip all three.
  function isRenderableAnnotation(ann: Annotation): boolean {
    const data = ann.data || {};
    if (ann.type === 'stamp') {
      // Need a stamp_type to drive colour + label. Without it the box is just
      // a default-grey rectangle with no content.
      if (!data.stamp_type) return false;
      return true;
    }
    if (ann.type === 'text') {
      const content = (data.content ?? '').toString().trim();
      return content.length > 0;
    }
    if (ann.type === 'freehand') {
      const points = data.points;
      return Array.isArray(points) && points.length >= 2;
    }
    return true;
  }

  function drawAnnotationOnCanvas(
    ctx: CanvasRenderingContext2D,
    ann: Annotation,
    w: number,
    h: number
  ) {
    const { type, data } = ann;

    if (type === 'text') {
      const x = normToCanvas(data.x || 0, w);
      const y = normToCanvas(data.y || 0, h);
      const content = data.content || '';
      const fontSize = data.fontSize || 13;
      const color = data.color || '#1a1aff';

      ctx.font = `${fontSize}px Arial, sans-serif`;
      const metrics = ctx.measureText(content);
      const textW = metrics.width;
      const textH = fontSize;
      const pad = 4;

      // Background
      ctx.fillStyle = 'rgba(255, 255, 200, 0.88)';
      ctx.fillRect(x - pad, y - textH - pad, textW + pad * 2, textH + pad * 2);
      ctx.strokeStyle = color;
      ctx.lineWidth = 1;
      ctx.strokeRect(x - pad, y - textH - pad, textW + pad * 2, textH + pad * 2);

      // Text
      ctx.fillStyle = color;
      ctx.fillText(content, x, y);

      // Actor badge — upper-right corner of the text bounding box
      drawActorBadge(ctx, ann, x + textW + pad, y - textH - pad);
    } else if (type === 'stamp') {
      const x = normToCanvas(data.x || 0, w);
      const y = normToCanvas(data.y || 0, h);
      const stampLabel = STAMP_LABELS[data.stamp_type as StampType] || data.stamp_type || 'STAMP';
      const color = STAMP_COLORS[data.stamp_type as StampType] || '#555555';
      const boxW = normToCanvas(data.width || 160, w);
      const boxH = 52;

      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.strokeRect(x, y - boxH, boxW, boxH);

      // Divider
      ctx.strokeStyle = color;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(x + 4, y - 28);
      ctx.lineTo(x + boxW - 4, y - 28);
      ctx.stroke();

      // Label
      ctx.fillStyle = color;
      ctx.font = 'bold 12px Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(stampLabel, x + boxW / 2, y - 32);

      // Architect name
      if (data.architect_name) {
        ctx.fillStyle = '#444';
        ctx.font = '9px Arial, sans-serif';
        ctx.fillText(data.architect_name, x + boxW / 2, y - 18);
      }

      // Date
      if (data.stamp_date) {
        ctx.fillStyle = '#666';
        ctx.font = '8px Arial, sans-serif';
        ctx.fillText(data.stamp_date, x + boxW / 2, y - 8);
      }

      ctx.textAlign = 'left';

      // Actor badge — upper-right corner of the stamp box
      drawActorBadge(ctx, ann, x + boxW, y - boxH);
    } else if (type === 'freehand') {
      const points: [number, number][] = data.points || [];
      if (points.length < 2) return;

      const color = data.color || '#e63946';
      const strokeWidth = data.strokeWidth || 2;

      ctx.strokeStyle = color;
      ctx.lineWidth = strokeWidth;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      ctx.moveTo(normToCanvas(points[0][0], w), normToCanvas(points[0][1], h));
      for (let i = 1; i < points.length; i++) {
        ctx.lineTo(normToCanvas(points[i][0], w), normToCanvas(points[i][1], h));
      }
      ctx.stroke();

      // Actor badge — anchored to the bounding box's upper-right corner.
      // Compute min/max in pixel space so the badge tracks freehand bounds.
      let minX = Infinity, minY = Infinity, maxX = -Infinity;
      for (const [px, py] of points) {
        const cx = normToCanvas(px, w);
        const cy = normToCanvas(py, h);
        if (cx < minX) minX = cx;
        if (cy < minY) minY = cy;
        if (cx > maxX) maxX = cx;
      }
      if (isFinite(maxX) && isFinite(minY)) {
        drawActorBadge(ctx, ann, maxX + BADGE_RADIUS, minY - BADGE_RADIUS);
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Mutations
  // ---------------------------------------------------------------------------

  const createAnnotation = useMutation({
    mutationFn: (annData: Parameters<typeof api.documentReviews.annotations.create>[1]) => {
      if (portalContext) {
        return api.portalReviews.createAnnotation(
          documentReviewId,
          annData,
          portalContext.viewToken,
          portalContext.actorId,
          portalContext.password,
        );
      }
      return api.documentReviews.annotations.create(documentReviewId, annData);
    },
    onSuccess: () => {
      // Invalidate using the same key shape as the useQuery above (admin vs portal).
      queryClient.invalidateQueries({
        queryKey: [
          'document-review-annotations',
          documentReviewId,
          portalContext ? `portal:${portalContext.actorId}` : 'admin',
        ],
      });
    },
    onError: (err: any) => {
      toast.error(err.message || 'Failed to save annotation');
    },
  });

  const deleteAnnotation = useMutation({
    mutationFn: (annotationId: string) => {
      // R1: portal customers cannot delete annotations. No portal DELETE endpoint
      // exists, so we no-op rather than calling the auth-gated admin path (which
      // would 401 silently and trigger a confusing toast).
      if (portalContext) {
        return Promise.resolve({ success: true });
      }
      return api.documentReviews.annotations.delete(documentReviewId, annotationId);
    },
    onSuccess: () => {
      if (portalContext) return; // no-op in portal mode
      queryClient.invalidateQueries({ queryKey: ['document-review-annotations', documentReviewId] });
      toast.success('Annotation deleted');
    },
    onError: (err: any) => {
      toast.error(err.message || 'Failed to delete annotation');
    },
  });

  // Undo = delete the most recent annotation on this page.
  // Disabled on portal — no DELETE endpoint, so undo is a no-op there.
  const handleUndo = () => {
    if (portalContext) return;
    const pageAnnotations = savedAnnotations
      .filter((a) => a.page_number === currentPage)
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

    if (pageAnnotations.length === 0) return;
    deleteAnnotation.mutate(pageAnnotations[0].id);
  };

  // ---------------------------------------------------------------------------
  // Canvas event handlers
  // ---------------------------------------------------------------------------

  function getCanvasCoords(e: React.MouseEvent<HTMLCanvasElement>): { x: number; y: number } {
    const canvas = overlayRef.current!;
    const rect = canvas.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  }

  function handleCanvasClick(e: React.MouseEvent<HTMLCanvasElement>) {
    if (mode === 'pointer') return;
    if (mode === 'freehand') return; // handled by mousedown/move/up

    const { x, y } = getCanvasCoords(e);
    const overlay = overlayRef.current!;
    const normX = canvasToNorm(x, overlay.width);
    const normY = canvasToNorm(y, overlay.height);

    if (mode === 'text') {
      // Place a text input at click position
      setPendingText({ x, y, pageNumber: currentPage });
      setTextInputValue('');
      setTimeout(() => textInputRef.current?.focus(), 50);
      return;
    }

    if (mode === 'stamp') {
      const architectName = portalContext
        ? getPortalActorName(portalContext.viewToken)
        : user?.name || user?.email || 'Architect';
      const stampDate = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD

      createAnnotation.mutate({
        page_number: currentPage,
        type: 'stamp',
        data: {
          x: normX,
          y: normY,
          stamp_type: stampType,
          architect_name: architectName,
          stamp_date: stampDate,
          width: 160,
        },
      });
    }
  }

  function handleMouseDown(e: React.MouseEvent<HTMLCanvasElement>) {
    if (mode !== 'freehand') return;
    setDrawing(true);
    const { x, y } = getCanvasCoords(e);
    const overlay = overlayRef.current!;
    setCurrentPath([[
      canvasToNorm(x, overlay.width),
      canvasToNorm(y, overlay.height),
    ]]);
  }

  function handleMouseMove(e: React.MouseEvent<HTMLCanvasElement>) {
    if (!drawing || mode !== 'freehand') return;
    const { x, y } = getCanvasCoords(e);
    const overlay = overlayRef.current!;

    setCurrentPath((prev) => [
      ...prev,
      [canvasToNorm(x, overlay.width), canvasToNorm(y, overlay.height)],
    ]);

    // Live preview on overlay canvas
    const ctx = overlay.getContext('2d')!;
    redrawAnnotations();
    if (currentPath.length >= 1) {
      ctx.strokeStyle = freehandColor;
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      ctx.moveTo(normToCanvas(currentPath[0][0], overlay.width), normToCanvas(currentPath[0][1], overlay.height));
      for (let i = 1; i < currentPath.length; i++) {
        ctx.lineTo(normToCanvas(currentPath[i][0], overlay.width), normToCanvas(currentPath[i][1], overlay.height));
      }
      ctx.lineTo(x, y);
      ctx.stroke();
    }
  }

  function handleMouseUp() {
    if (!drawing || mode !== 'freehand') return;
    setDrawing(false);

    if (currentPath.length >= 2) {
      createAnnotation.mutate({
        page_number: currentPage,
        type: 'freehand',
        data: {
          points: currentPath,
          color: freehandColor,
          strokeWidth: 2,
        },
      });
    }
    setCurrentPath([]);
  }

  function handleTextSubmit() {
    if (!pendingText || !textInputValue.trim()) {
      setPendingText(null);
      return;
    }

    const overlay = overlayRef.current!;
    const normX = canvasToNorm(pendingText.x, overlay.width);
    const normY = canvasToNorm(pendingText.y, overlay.height);

    createAnnotation.mutate({
      page_number: currentPage,
      type: 'text',
      data: {
        x: normX,
        y: normY,
        content: textInputValue.trim(),
        fontSize: 13,
        color: '#1a1aff',
      },
    });

    setPendingText(null);
    setTextInputValue('');
  }

  // ---------------------------------------------------------------------------
  // Approve fast-path (D-Q5 AC#6)
  // When reviewer clicks a stamp with no existing annotations → confirm dialog.
  // On confirm: empty-annotation review → outcome=approved, status=completed.
  // ---------------------------------------------------------------------------

  const approveFastPath = useMutation({
    mutationFn: async () => {
      // F1: when a condition note was captured during stamp-free approval,
      // persist it as a text annotation on page 1 so the audit trail and the
      // sealed PDF flow both retain the architect's wording. Empty note
      // skips this step (no orphan annotations).
      //
      // Phase 4 R3: in portal context, route the annotation create + the
      // status transitions through the public portal endpoints. The admin
      // endpoints require Firebase auth and 401 on portal customers — that
      // was silently turning the Confirm-Approval dialog into a no-op.
      if (!inCanvasStampsEnabled && conditionNote.trim()) {
        try {
          const annotationPayload = {
            page_number: 1,
            type: 'text' as const,
            coords: { x: 50, y: 50 },
            data: {
              text: conditionNote.trim(),
              source: 'confirm_approval_condition_note',
            },
          };
          if (portalContext) {
            await api.portalReviews.createAnnotation(
              documentReviewId,
              annotationPayload,
              portalContext.viewToken,
              portalContext.actorId,
              portalContext.password,
            );
          } else {
            await api.documentReviews.annotations.create(documentReviewId, annotationPayload);
          }
        } catch (noteErr) {
          // Don't fail the whole approval because the note couldn't be
          // attached — log + continue. Operator sees the toast either way.
          console.warn('Failed to attach condition note to review:', noteErr);
        }
      }
      // Transition to active with approved outcome, then immediately complete.
      // Portal route uses the token-gated PATCH /api/portal/reviews/{id}/status
      // mirror added in Phase 4 R3.
      if (portalContext) {
        await api.portalReviews.updateStatus(
          documentReviewId,
          { status: 'active', outcome: 'approved' },
          portalContext.viewToken,
          portalContext.actorId,
          portalContext.password,
        );
        await api.portalReviews.updateStatus(
          documentReviewId,
          { status: 'completed', outcome: 'approved' },
          portalContext.viewToken,
          portalContext.actorId,
          portalContext.password,
        );
      } else {
        await api.documentReviews.updateStatus(documentReviewId, {
          status: 'active',
          outcome: 'approved',
        });
        await api.documentReviews.updateStatus(documentReviewId, {
          status: 'completed',
          outcome: 'approved',
        });
      }
    },
    onSuccess: () => {
      toast.success(
        !inCanvasStampsEnabled ? 'Review approved' : 'Approved without markup'
      );
      queryClient.invalidateQueries({ queryKey: ['document-review-annotations', documentReviewId] });
      setApproveFastPathOpen(false);
      setConditionNote('');
      onClose?.();
    },
    onError: (err: any) => {
      toast.error(err.message || 'Approve fast-path failed');
      setApproveFastPathOpen(false);
    },
  });

  // Intercept stamp clicks in approve mode with no annotations → fast-path dialog
  const handleStampClick = () => {
    if (reviewMode === 'approve' && savedAnnotations.length === 0) {
      setApproveFastPathOpen(true);
    }
    // Otherwise normal stamp placement via handleCanvasClick
  };

  // ---------------------------------------------------------------------------
  // Export / Send
  // ---------------------------------------------------------------------------

  const handleExportPdf = async () => {
    setExporting(true);
    try {
      await api.documentReviews.annotations.exportFlatPdf(documentReviewId);
      toast.success('Annotated PDF downloaded');
    } catch (err: any) {
      toast.error(err.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  // Portal customer download handler — token-gated, mirrors handleExportPdf
  // but pulls the sealed PDF bytes (or original) via the portal endpoint.
  const [portalDownloading, setPortalDownloading] = useState(false);
  const handlePortalDownload = async () => {
    if (!portalContext) return;
    setPortalDownloading(true);
    try {
      // Filename hint: derive from review id; the backend already serves the
      // externally-stamped variant once it exists, so the user always gets the
      // most recent bytes.
      await api.portalReviews.downloadFile(
        documentReviewId,
        portalContext.viewToken,
        portalContext.actorId,
        `document-review-${documentReviewId.slice(0, 8)}.pdf`,
        portalContext.password,
      );
      toast.success('Download started');
    } catch (err: any) {
      toast.error(err.message || 'Download failed');
    } finally {
      setPortalDownloading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Cursor styling based on mode
  // ---------------------------------------------------------------------------

  const cursorStyle: Record<AnnotationMode, string> = {
    pointer: 'default',
    text: 'text',
    stamp: 'crosshair',
    freehand: 'crosshair',
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="flex flex-col h-full bg-muted/20">
      {/* Toolbar — reviewMode controls which tools are visible (D-Q22) */}
      <AnnotationToolbar
        mode={mode}
        stampType={stampType}
        freehandColor={freehandColor}
        onModeChange={(m) => {
          // Intercept stamp selection in approve mode with no annotations
          if (m === 'stamp' && reviewMode === 'approve' && savedAnnotations.length === 0) {
            setApproveFastPathOpen(true);
            return;
          }
          setMode(m);
        }}
        onStampTypeChange={setStampType}
        onColorChange={setFreehandColor}
        onUndo={handleUndo}
        onExportPdf={handleExportPdf}
        onSendForReview={() => setSendDialogOpen(true)}
        onConfirmApproval={() => setApproveFastPathOpen(true)}
        canUndo={savedAnnotations.some((a) => a.page_number === currentPage)}
        exporting={exporting}
        reviewMode={reviewMode}
        isPortal={isPortal}
        onDownloadPortalFile={isPortal && portalContext ? handlePortalDownload : undefined}
        portalDownloading={portalDownloading}
        inCanvasStampsEnabled={inCanvasStampsEnabled}
        viewOnly={reviewStatus === 'locked'}
      />

      {/* R2: External stamp upload action (gated by flags + role + status).
       *  R6 is internal-only per spec — never render in portal context. */}
      {!isPortal && (
        <ExternalStampSection
          documentReviewId={documentReviewId}
          reviewStatus={reviewStatus}
          externallyStampedPdfRef={externallyStampedPdfRef}
          auditCertPdfRef={auditCertPdfRef}
          onConfirmed={onClose}
        />
      )}

      {/* R5 AC#2: lazy-render banner for documents >50 pages */}
      {numPages > 50 && (
        <div
          className="flex items-center gap-2 px-4 py-2 bg-amber-50 border-b border-amber-200 text-amber-800 text-sm"
          role="status"
          aria-live="polite"
          data-testid="lazy-render-banner"
        >
          <span className="font-medium">Large document</span>
          <span className="text-amber-700">— pages load as you scroll</span>
        </div>
      )}

      {/* Page controls */}
      {numPages > 1 && (
        <div className="flex items-center justify-center gap-3 py-1.5 bg-background border-b text-sm">
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs"
            disabled={currentPage <= 1}
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
          >
            ‹ Prev
          </Button>
          <span className="text-muted-foreground">
            Page {currentPage} of {numPages}
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs"
            disabled={currentPage >= numPages}
            onClick={() => setCurrentPage((p) => Math.min(numPages, p + 1))}
          >
            Next ›
          </Button>
          <div className="flex items-center gap-1 ml-2">
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs"
              onClick={() => setRenderScale((s) => Math.max(0.5, s - 0.25))}
            >
              −
            </Button>
            <span className="text-xs text-muted-foreground w-12 text-center">
              {Math.round(renderScale * 100)}%
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs"
              onClick={() => setRenderScale((s) => Math.min(3, s + 0.25))}
            >
              +
            </Button>
          </div>
          {onClose && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 ml-2"
              onClick={onClose}
              title="Close viewer"
            >
              <X className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      )}

      {/* PDF canvas area */}
      <div className="flex-1 overflow-auto flex justify-center p-4">
        {pdfLoading && (
          <div className="flex items-center gap-2 text-muted-foreground py-12">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>Loading PDF…</span>
          </div>
        )}

        {pdfError && (
          <div className="py-12 text-destructive text-sm text-center">
            <p>Failed to load PDF: {pdfError}</p>
          </div>
        )}

        {!pdfLoading && !pdfError && (
          <div className="relative inline-block shadow-lg">
            {/* PDF canvas */}
            <canvas ref={canvasRef} className="block" />

            {/* Annotation overlay canvas */}
            <canvas
              ref={overlayRef}
              className="absolute inset-0"
              style={{
                cursor: cursorStyle[mode],
                opacity: 0.9,
              }}
              onClick={handleCanvasClick}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            />

            {/* Inline text input for text annotations */}
            {pendingText && (
              <div
                className="absolute"
                style={{
                  left: pendingText.x,
                  top: pendingText.y - 28,
                  zIndex: 10,
                }}
              >
                <input
                  ref={textInputRef}
                  type="text"
                  value={textInputValue}
                  onChange={(e) => setTextInputValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleTextSubmit();
                    if (e.key === 'Escape') {
                      setPendingText(null);
                      setTextInputValue('');
                    }
                  }}
                  onBlur={handleTextSubmit}
                  placeholder="Type comment…"
                  className="border border-blue-400 rounded px-1 py-0.5 text-sm bg-yellow-50 shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-400 min-w-32"
                  style={{ fontSize: 13 }}
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Draft → Send dialog (D-Q5) */}
      <DocumentReviewSendDialog
        open={sendDialogOpen}
        onClose={() => setSendDialogOpen(false)}
        documentReviewId={documentReviewId}
        projectId={projectId || ''}
      />

      {/* R5 AC#2 / D-Q9: page-cap soft-warn (51-200 pages) */}
      <AlertDialog open={pageCapWarnOpen} onOpenChange={setPageCapWarnOpen}>
        <AlertDialogContent data-testid="page-cap-warn-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Large document</AlertDialogTitle>
            <AlertDialogDescription>
              This document has {numPages} pages. Pages load as you scroll — performance
              may be affected on slower devices. Consider splitting documents over 200 pages.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={onClose}>Close viewer</AlertDialogCancel>
            <AlertDialogAction onClick={() => setPageCapWarnOpen(false)}>
              Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Approve fast-path confirmation (D-Q5 AC#6 + F1)
       *  Two variants share this dialog:
       *  - in_canvas_stamps_enabled=true (legacy): "Approve without markup?"
       *  - in_canvas_stamps_enabled=false (F1):    "Confirm approval" + optional
       *    condition note. Marks the review approved without an in-canvas stamp.
       */}
      <AlertDialog open={approveFastPathOpen} onOpenChange={setApproveFastPathOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {inCanvasStampsEnabled ? 'Approve without markup?' : 'Confirm approval'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {inCanvasStampsEnabled ? (
                <>No annotations have been added. Approve this document without any markup?
                This will mark the review as Approved and complete it immediately.</>
              ) : (
                <>Mark this review as Approved? An optional condition note will be
                recorded in the audit trail alongside this approval. The signed PDF
                will be sealed externally; no in-canvas stamp is applied.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>

          {!inCanvasStampsEnabled && (
            <div className="space-y-2 py-2">
              <label className="text-xs font-medium text-muted-foreground" htmlFor="confirm-approval-condition-note">
                Condition note (optional)
              </label>
              <textarea
                id="confirm-approval-condition-note"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[80px]"
                placeholder="e.g. Approved subject to: site-confirm 52'-0&quot; foundation dimension before placing concrete."
                value={conditionNote}
                onChange={(e) => setConditionNote(e.target.value)}
                maxLength={500}
                data-testid="condition-note-input"
              />
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setConditionNote('')}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => approveFastPath.mutate()}
              disabled={approveFastPath.isPending}
              data-testid="confirm-approval-submit"
            >
              {approveFastPath.isPending
                ? 'Approving…'
                : (inCanvasStampsEnabled ? 'Approve' : 'Confirm approval')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
